import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { useCallback } from 'react';
import { useRealtimeScope } from '@/shared/hooks/useRealtimeScope';
import { useSupabaseRealtime } from '@/shared/hooks/useSupabaseRealtime';
import type { Tables } from '@/shared/types/database';
import { rowToTask } from '../services/supabaseTodoService';
import { useTodoStore } from '../stores/todoStore';

type TaskRow = Tables<'tasks'>;

/**
 * Realtime tasks subscription (P7).
 *
 * Streams `public.tasks` changes for the current family into the todo store
 * via the local-only `upsertRealtimeTask` / `deleteRealtimeTask` setters (no
 * service round-trips — an echo must never re-issue the write that produced
 * it). A no-op when realtime is disabled (see {@link useSupabaseRealtime}).
 *
 * @module features/todo-list/hooks/useRealtimeTasks
 */
export function useRealtimeTasks(): void {
  const { familyId } = useRealtimeScope();
  const upsertRealtimeTask = useTodoStore((s) => s.upsertRealtimeTask);
  const deleteRealtimeTask = useTodoStore((s) => s.deleteRealtimeTask);
  const refetch = useTodoStore((s) => s.refetch);

  const handleChange = useCallback(
    (payload: RealtimePostgresChangesPayload<TaskRow>) => {
      if (payload.eventType === 'DELETE') {
        const oldId = payload.old.id;
        if (oldId) {
          deleteRealtimeTask(oldId);
        }
        return;
      }

      const row = payload.new;
      // Defense in depth: ignore rows outside the current family.
      if (familyId !== null && row.family_id !== familyId) {
        return;
      }
      upsertRealtimeTask(rowToTask(row));
    },
    [familyId, upsertRealtimeTask, deleteRealtimeTask]
  );

  useSupabaseRealtime<TaskRow>({
    table: 'tasks',
    enabled: familyId !== null,
    ...(familyId !== null && { filter: `family_id=eq.${familyId}` }),
    onChange: handleChange,
    // Changes streamed while the channel was down are lost — refetch on
    // recovery to close the gap.
    onReconnect: () => void refetch(),
  });
}
